import { useState, useEffect, useRef, useCallback } from 'react'
import { Box, Flex, Text, VStack, Input, Spinner, ConfirmDialog, useT } from '@minetrace/ui'
import { useFormColors, FormSection } from '@minetrace/ui/forms'
import {
  LuUsers as Users,
  LuSearch as Search,
  LuUserPlus as UserPlus,
  LuTrash2 as Trash2,
} from 'react-icons/lu'
import { toaster } from '@minetrace/ui'
import { programacionesService } from '../../../api/services'
import type { ScheduleGeologistResponse, GeologistOptionResponse } from '../../../api/models/response/programacion'

interface Step2Props {
  scheduleId: string | null
  geologists: ScheduleGeologistResponse[]
  onGeologistsChange: (geologists: ScheduleGeologistResponse[]) => void
}

const Step2Geologos = ({ scheduleId, geologists, onGeologistsChange }: Step2Props) => {
  const c = useFormColors()
  const { t } = useT()

  const [searchResults, setSearchResults] = useState<GeologistOptionResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [adding, setAdding] = useState<string | null>(null)
  const [removeTarget, setRemoveTarget] = useState<ScheduleGeologistResponse | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const inputStyles = {
    bg: c.bgInput,
    border: '1px solid',
    borderColor: 'gray.200',
    borderRadius: 'lg',
    color: c.text,
    h: '42px',
    fontSize: 'sm',
    _placeholder: { color: c.textMuted },
    _focus: { outline: 'none', borderColor: '#b4532a', ring: '1px', ringColor: '#b4532a' },
  } as const

  const fetchGeologists = useCallback(async (search: string, pageNum: number, append: boolean) => {
    setLoading(true)
    try {
      const result = await programacionesService.getGeologists({ search: search || undefined, page: pageNum, pageSize: 10 })
      if (append) {
        setSearchResults((prev) => [...prev, ...result.items])
      } else {
        setSearchResults(result.items)
      }
      setHasMore(result.hasNextPage)
      setPage(pageNum)
    } catch {
      // handled
    } finally {
      setLoading(false)
    }
  }, [])

  // Initial load
  useEffect(() => {
    fetchGeologists('', 1, false)
  }, [fetchGeologists])

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      fetchGeologists(searchText, 1, false)
    }, 300)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [searchText, fetchGeologists])

  const handleLoadMore = () => {
    fetchGeologists(searchText, page + 1, true)
  }

  const addedIds = new Set(geologists.map((g) => g.geologistId))

  const handleAdd = async (geologist: GeologistOptionResponse) => {
    if (!scheduleId) {
      toaster.error({ description: t('programaciones.wizard.toasts.completeStep1First') })
      return
    }
    setAdding(geologist.geologistId)
    try {
      const result = await programacionesService.addGeologist(scheduleId, geologist.geologistId)
      onGeologistsChange([...geologists, result])
    } catch {
      // handled
    } finally {
      setAdding(null)
    }
  }

  const handleConfirmRemove = async () => {
    if (!scheduleId || !removeTarget) return
    try {
      await programacionesService.removeGeologist(scheduleId, removeTarget.id)
      onGeologistsChange(geologists.filter((g) => g.id !== removeTarget.id))
    } catch {
      // handled
    } finally {
      setRemoveTarget(null)
    }
  }

  const getInitials = (name: string) => {
    const parts = name.split(' ').filter(Boolean)
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    return (parts[0]?.[0] ?? '?').toUpperCase()
  }

  return (
    <VStack gap={6} alignItems="stretch">
      <FormSection icon={Users} titleTid="programaciones.wizard.step2.sectionTitle" descriptionTid="programaciones.wizard.step2.sectionDescription">
        <Flex gap={6} direction={{ base: 'column', lg: 'row' }}>
          {/* Left: Search & Results */}
          <VStack gap={4} alignItems="stretch" flex={1}>
            <Box>
              <Text tid="programaciones.wizard.step2.searchLabel" fontSize="sm" fontWeight="600" color={c.text} mb={2} />
              <Flex gap={2}>
                <Input
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value.slice(0, 200))}
                  placeholder={t('programaciones.wizard.step2.searchPlaceholder')}
                  maxLength={200}
                  {...inputStyles}
                />
                <Flex alignItems="center" justifyContent="center" px={3} h="42px" bg="#b4532a" color="white" borderRadius="lg" flexShrink={0}>
                  <Search size={16} />
                </Flex>
              </Flex>
            </Box>

            {searchResults.length === 0 && !loading ? (
              <Text tid="programaciones.wizard.step2.noResults" fontSize="sm" color={c.textMuted} textAlign="center" py={4} />
            ) : (
              <VStack gap={2} alignItems="stretch" maxH="400px" overflowY="auto">
                {searchResults.map((g) => {
                  const isAdded = addedIds.has(g.geologistId)
                  const isAddingThis = adding === g.geologistId
                  return (
                    <Flex
                      key={g.geologistId}
                      bg="white" border="1px solid" borderColor={isAdded ? '#dcefed' : 'gray.100'}
                      borderRadius="lg" p={3} alignItems="center" gap={3}
                      shadow="0 1px 2px rgba(0,0,0,0.04)" opacity={isAdded ? 0.6 : 1}
                    >
                      <Flex w="40px" h="40px" borderRadius="full" bg="#dcefed" color="#1d6664"
                        alignItems="center" justifyContent="center" fontSize="sm" fontWeight="700" flexShrink={0}>
                        {getInitials(g.fullName)}
                      </Flex>
                      <Box flex={1} minW={0}>
                        <Text fontSize="sm" fontWeight="600" color={c.text} truncate>{g.fullName}</Text>
                        <Flex gap={3}>
                          {g.identityDoc && <Text fontSize="xs" color={c.textMuted} fontFamily="monospace">{g.identityDoc}</Text>}
                          {g.colegiatureCode && <Text tid="programaciones.wizard.step2.cip" values={{ code: g.colegiatureCode }} fontSize="xs" color={c.textMuted} />}
                        </Flex>
                      </Box>
                      {!isAdded ? (
                        <Box as="button" type="button" display="flex" alignItems="center" gap={1}
                          px={3} py={1.5} bg="#b4532a" color="white" borderRadius="lg" fontSize="xs" fontWeight="600"
                          cursor={isAddingThis ? 'not-allowed' : 'pointer'} opacity={isAddingThis ? 0.5 : 1}
                          flexShrink={0} _hover={{ bg: '#085a30' }}
                          onClick={() => handleAdd(g)} disabled={isAddingThis}>
                          {isAddingThis ? <Spinner size="xs" color="white" /> : <UserPlus size={14} />}
                          {t('programaciones.wizard.step2.addButton')}
                        </Box>
                      ) : (
                        <Text tid="programaciones.wizard.step2.added" fontSize="xs" color="#1d6664" fontWeight="600" flexShrink={0} />
                      )}
                    </Flex>
                  )
                })}
              </VStack>
            )}

            {loading && <Flex justifyContent="center" py={2}><Spinner size="sm" color="#1d6664" /></Flex>}

            {hasMore && !loading && (
              <Box as="button" type="button" w="100%" py={2} textAlign="center" fontSize="sm" fontWeight="600"
                color="#1d6664" cursor="pointer" _hover={{ textDecoration: 'underline' }}
                onClick={handleLoadMore}>
                {t('programaciones.wizard.step2.loadMore')}
              </Box>
            )}
          </VStack>

          {/* Right: Selected */}
          <Box minW={{ lg: '320px' }} maxW={{ lg: '400px' }}>
            <Text tid="programaciones.wizard.step2.assignedTitle" values={{ count: geologists.length }} fontSize="xs" fontWeight="700" color={c.textMuted} letterSpacing="0.05em" textTransform="uppercase" mb={3} />
            {geologists.length === 0 ? (
              <VStack gap={3}>
                {[1, 2, 3].map((i) => (
                  <Flex key={i} p={3} borderRadius="lg" border="1px dashed" borderColor="gray.200"
                    alignItems="center" justifyContent="center" h="60px" w="100%">
                    <Text tid="programaciones.wizard.step2.availableSlot" fontSize="xs" color={c.textMuted} />
                  </Flex>
                ))}
              </VStack>
            ) : (
              <VStack gap={2} alignItems="stretch">
                {geologists.map((g) => (
                  <Flex key={g.id} bg="white" border="1px solid" borderColor="gray.100" borderRadius="lg"
                    p={3} alignItems="center" gap={3} shadow="0 1px 2px rgba(0,0,0,0.04)">
                    <Flex w="40px" h="40px" borderRadius="full" bg="#dcefed" color="#1d6664"
                      alignItems="center" justifyContent="center" fontSize="sm" fontWeight="700" flexShrink={0}>
                      {getInitials(g.fullName)}
                    </Flex>
                    <Box flex={1} minW={0}>
                      <Text fontSize="sm" fontWeight="600" color={c.text} truncate>{g.fullName}</Text>
                      <Flex gap={3}>
                        {g.identityDoc && <Text fontSize="xs" color={c.textMuted} fontFamily="monospace">{g.identityDoc}</Text>}
                        {g.colegiatureCode && <Text tid="programaciones.wizard.step2.cip" values={{ code: g.colegiatureCode }} fontSize="xs" color={c.textMuted} />}
                      </Flex>
                    </Box>
                    <Box as="button" type="button" fontSize="xs" color="#b23a26" cursor="pointer" fontWeight="600"
                      flexShrink={0} _hover={{ textDecoration: 'underline' }}
                      onClick={() => setRemoveTarget(g)}>
                      <Trash2 size={14} />
                    </Box>
                  </Flex>
                ))}
              </VStack>
            )}
          </Box>
        </Flex>
      </FormSection>

      <ConfirmDialog
        open={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        onConfirm={handleConfirmRemove}
        title={t('programaciones.wizard.step2.removeTitle')}
        confirmLabel={t('programaciones.wizard.step2.removeConfirm')}
        variant="danger"
      >
        {t('programaciones.wizard.step2.removeQuestionPrefix')} <Text as="span" fontWeight="600">{removeTarget?.fullName}</Text>{t('programaciones.wizard.step2.removeQuestionSuffix')}
      </ConfirmDialog>
    </VStack>
  )
}

export default Step2Geologos
